import { BarChart3, ClipboardList, TrendingUp, Users } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { DataTable } from '../../components/common/DataTable';
import { LoadingState } from '../../components/common/LoadingState';
import { PageHeader } from '../../components/common/PageHeader';
import { StatCard } from '../../components/dashboard/StatCard';
import { StatusBadge } from '../../components/common/StatusBadge';
import { getCourseSignups, getCourses, getStudents } from '../../firebase/firestore';
import { useTenant } from '../../hooks/useTenant';

function normalizeText(value) {
  return String(value || '').trim().toLowerCase();
}

function formatStudentName(item) {
  return item?.fullName || `${item?.firstName || ''} ${item?.lastName || ''}`.trim() || '-';
}

function toDate(value) {
  if (!value) return null;
  if (value?.toDate) return value.toDate();
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function getMonthKey(date) {
  if (!date) return 'Unknown';
  return date.toLocaleDateString('en-CA', { month: 'short', year: 'numeric' });
}

function getInitials(name = '') {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

function normalizeStatus(status) {
  return normalizeText(status).replace(/[_\s]+/g, '-');
}

function getTrendSeries(entries, windowSize = 6) {
  const months = [];
  const now = new Date();
  for (let index = windowSize - 1; index >= 0; index -= 1) {
    const date = new Date(now.getFullYear(), now.getMonth() - index, 1);
    months.push(getMonthKey(date));
  }

  return months.map((label) => {
    const count = entries.filter((item) => getMonthKey(toDate(item?.createdAt) || toDate(item?.updatedAt) || toDate(item?.admissionDate)) === label).length;
    return { label, value: count };
  });
}

function SparklineChart({ data, title }) {
  const width = 520;
  const height = 180;
  const padding = 18;

  if (!data.length) {
    return <div className="analytics-chartEmpty">No trend data yet.</div>;
  }

  const values = data.map((item) => item.value);
  const max = Math.max(...values, 1);
  const range = max === 0 ? 1 : max;
  const points = data.map((item, index) => {
    const x = padding + (index * (width - padding * 2)) / Math.max(data.length - 1, 1);
    const y = height - padding - ((item.value / range) * (height - padding * 2));
    return { ...item, x, y };
  });

  const linePath = points.map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`).join(' ');
  const areaPath = `${linePath} L ${points[points.length - 1].x} ${height - padding} L ${points[0].x} ${height - padding} Z`;

  return (
    <div className="analyticsSparkline">
      <div className="analyticsChartHeader">
        <div>
          <span>{title}</span>
          <strong>{data.reduce((sum, item) => sum + item.value, 0)} total</strong>
        </div>
      </div>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label={title}>
        <defs>
          <linearGradient id="student-trend-fill" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#0f766e" stopOpacity="0.24" />
            <stop offset="100%" stopColor="#0f766e" stopOpacity="0.02" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((ratio) => (
          <line
            key={ratio}
            x1={padding}
            x2={width - padding}
            y1={height - padding - ((height - padding * 2) * ratio)}
            y2={height - padding - ((height - padding * 2) * ratio)}
            className="analyticsGridLine"
          />
        ))}
        <path d={areaPath} fill="url(#student-trend-fill)" />
        <path d={linePath} fill="none" stroke="#0f766e" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((point) => (
          <g key={point.label}>
            <circle cx={point.x} cy={point.y} r="4.5" fill="#ffffff" stroke="#0f766e" strokeWidth="3" />
          </g>
        ))}
      </svg>
      <div className="analyticsChartLabels">
        {points.map((point) => (
          <div key={point.label}>
            <span>{point.label}</span>
            <strong>{point.value}</strong>
          </div>
        ))}
      </div>
    </div>
  );
}

function DonutChart({ data, title, totalLabel }) {
  const total = data.reduce((sum, item) => sum + item.value, 0);
  const circumference = 264;
  let offset = 0;

  return (
    <div className="analyticsDonutPanel">
      <div className="analyticsChartHeader">
        <div>
          <span>{title}</span>
          <strong>{totalLabel}</strong>
        </div>
      </div>
      <div className="analyticsDonutWrap">
        <svg viewBox="0 0 120 120" role="img" aria-label={title}>
          <circle cx="60" cy="60" r="42" className="analyticsDonutTrack" />
          {data.filter((item) => item.value > 0).map((item) => {
            const dashArray = `${(item.value / Math.max(total, 1)) * circumference} ${circumference}`;
            const node = (
              <circle
                key={item.label}
                cx="60"
                cy="60"
                r="42"
                fill="none"
                stroke={item.color}
                strokeWidth="12"
                strokeDasharray={dashArray}
                strokeDashoffset={-offset}
                strokeLinecap="round"
                transform="rotate(-90 60 60)"
              />
            );
            offset += (item.value / Math.max(total, 1)) * circumference;
            return node;
          })}
        </svg>
        <div className="analyticsDonutCenter">
          <strong>{total}</strong>
          <span>Records</span>
        </div>
      </div>
      <div className="analyticsLegend">
        {data.map((item) => (
          <div key={item.label}>
            <span className="analyticsLegendSwatch" style={{ background: item.color }} />
            <div>
              <strong>{item.label}</strong>
              <span>{item.value}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HorizontalBars({ title, rows }) {
  const max = Math.max(...rows.map((item) => item.value), 1);

  return (
    <div className="analyticsBarsPanel">
      <div className="analyticsChartHeader">
        <div>
          <span>{title}</span>
          <strong>Top movers</strong>
        </div>
      </div>
      <div className="analyticsBars">
        {rows.length ? rows.map((item) => (
          <div key={item.label} className="analyticsBarRow">
            <div className="analyticsBarMeta">
              <strong>{item.label}</strong>
              <span>{item.value}</span>
            </div>
            <div className="analyticsBarTrack">
              <div className="analyticsBarFill" style={{ width: `${(item.value / max) * 100}%`, background: item.color || '#0f766e' }} />
            </div>
          </div>
        )) : (
          <div className="analyticsChartEmpty">No course data yet.</div>
        )}
      </div>
    </div>
  );
}

export function StudentsPage() {
  const { activeTenantId } = useTenant();
  const [students, setStudents] = useState([]);
  const [signups, setSignups] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const pendingSignups = signups.filter((item) => String(item.status || '').toLowerCase() !== 'student');

  const load = useCallback(async () => {
    setLoading(true);
    const [nextStudents, nextSignups, nextCourses] = await Promise.all([getStudents(activeTenantId), getCourseSignups(activeTenantId), getCourses(activeTenantId)]);
    setStudents(nextStudents);
    setSignups(nextSignups);
    setCourses(nextCourses);
    setLoading(false);
  }, [activeTenantId]);

  useEffect(() => {
    load().catch((error) => {
      console.error(error);
      setLoading(false);
    });
  }, [load]);

  const analytics = useMemo(() => {
    const activeStudents = signups.filter((item) => normalizeStatus(item.status) === 'student');
    const learningBreaks = signups.filter((item) => normalizeStatus(item.status) === 'learning-break');
    const graduated = signups.filter((item) => normalizeStatus(item.status) === 'graduated');
    const dropped = signups.filter((item) => ['dropped', 'expelled', 'did-not-join', 'never-joined', 'not-joined', 'not-joining'].includes(normalizeStatus(item.status)));
    const pending = signups.filter((item) => normalizeStatus(item.status) !== 'student');
    const statusSeries = [
      { label: 'Active', value: activeStudents.length, color: '#0f766e' },
      { label: 'Pending', value: pending.length, color: '#f59e0b' },
      { label: 'Break', value: learningBreaks.length, color: '#2563eb' },
      { label: 'Graduated', value: graduated.length, color: '#16a34a' },
      { label: 'Dropped', value: dropped.length, color: '#dc2626' },
    ];

    const monthlyTrend = getTrendSeries(signups.filter((item) => toDate(item.createdAt) || toDate(item.updatedAt) || toDate(item.admissionDate)), 6);

    const courseCounts = Array.from(
      signups.reduce((map, item) => {
        const courseName = item.courseName || courses.find((course) => course.id === item.courseId)?.name || 'Unassigned';
        map.set(courseName, (map.get(courseName) || 0) + 1);
        return map;
      }, new Map()).entries(),
    )
      .map(([label, value]) => ({ label, value }))
      .sort((a, b) => b.value - a.value)
      .slice(0, 5)
      .map((item, index) => ({
        ...item,
        color: ['#0f766e', '#2563eb', '#8b5cf6', '#f59e0b', '#dc2626'][index % 5],
      }));

    return {
      totalStudents: students.length + activeStudents.length,
      activeStudents: activeStudents.length,
      pendingApplications: pending.length,
      learningBreaks: learningBreaks.length,
      graduated: graduated.length,
      dropped: dropped.length,
      statusSeries,
      monthlyTrend,
      courseCounts,
    };
  }, [courses, signups, students.length]);

  return (
    <div className="page-stack operations-page students-page">
      <PageHeader title="Students" description="Manage learner records for your organization." />
      <section className="studentAnalyticsSection">
        <div className="panel-heading">
          <div>
            <h2>Analytics Snapshot</h2>
            <span>Live student lifecycle, course demand, and application trend data.</span>
          </div>
        </div>
        <div className="studentAnalyticsStats">
          <StatCard label="Student profiles" value={students.length} icon={Users} caption="Manual and imported learners" />
          <StatCard label="Active students" value={analytics.activeStudents} icon={TrendingUp} caption="Admitted and learning now" />
          <StatCard label="Applications" value={pendingSignups.length} icon={ClipboardList} caption="Waiting for review" />
          <StatCard label="Total records" value={analytics.totalStudents} icon={BarChart3} caption="Student and signup totals" />
        </div>
        <div className="studentAnalyticsGrid">
          <article className="studentAnalyticsCard">
            <DonutChart
              title="Student lifecycle"
              totalLabel="Status mix"
              data={analytics.statusSeries}
            />
          </article>
          <article className="studentAnalyticsCard">
            <SparklineChart title="Applications over time" data={analytics.monthlyTrend} />
          </article>
          <article className="studentAnalyticsCard studentAnalyticsCard--full">
            <HorizontalBars title="Most requested courses" rows={analytics.courseCounts} />
          </article>
        </div>
      </section>
      {loading ? <LoadingState label="Loading students..." /> : (
        <>
          <DataTable
            rows={students}
            columns={[
              {
                key: 'name',
                label: 'Name',
                render: (item) => {
                  const name = formatStudentName(item);
                  return (
                    <div className="appsStudentCell">
                      <div className="application-avatar" aria-hidden="true">{getInitials(name)}</div>
                      <div className="application-applicant-copy">
                        <strong>{name}</strong>
                        <span>{item.email || 'No email yet'}</span>
                      </div>
                    </div>
                  );
                },
              },
              { key: 'email', label: 'Email', render: (item) => item.email || '-' },
              { key: 'phone', label: 'Phone', render: (item) => item.phone || '-' },
              { key: 'status', label: 'Status', render: (item) => <StatusBadge status={item.status} /> },
            ]}
            empty={(
              <div className="empty-state">
                <div className="empty-icon" aria-hidden="true"><Users size={28} /></div>
              </div>
            )}
          />
        </>
      )}
    </div>
  );
}
